import {
  ForwardRefRenderFunction,
  FunctionComponent,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useState,
} from "react";
import { diffLines } from "diff";
import { ThemedToken } from "shiki";
import polarSyntaxHighlighting from "@/src/polarSyntaxHighlighting";
import { useIsDark } from "@/src/theme/useIsDark";

export const BACKGROUND_COLOR = "#f2f6fb";
// Matches shiki's "min-dark" editor background, for dark code panels.
export const BACKGROUND_COLOR_DARK = "#1f1f1f";

export const THEME = {
  'code[class*="language-"], pre[class*="language-"]': {
    color: BACKGROUND_COLOR,
  },
  keyword: {
    color: "#ff0078",
    fontWeight: "bold",
  },
  comment: {
    color: "#999",
  },
  string: {
    color: "#028265",
  },
  url: {
    color: "#028265",
  },
  "attr-value": {
    color: "#028265",
  },
  variable: {
    color: "#0076ff",
  },
  "language-javascript": {
    color: "#0076ff",
  },
  "attr-name": {
    color: "#d9931e",
    fontStyle: "normal",
  },
  punctuation: {
    color: "#333",
  },
  operator: {
    color: "#333",
  },
  number: {
    color: "#0076ff",
  },
  function: {
    color: "#0076ff",
    fontWeight: "bold",
  },
  tag: {
    color: "#0076ff",
  },
  boolean: {
    color: "#d9931e",
  },
  regex: {
    color: "#d9931e",
  },
};

const PolicyComponent: ForwardRefRenderFunction<
  {
    scrollToLine: (lineNumber: number) => void;
  },
  {
    policy: string | null;
    lines?: number[] | null;
    scrollToLine?: number;
  }
> = ({ policy, lines, scrollToLine }, ref) => {
  const [parsedPolicy, setParsedPolicy] = useState<ThemedToken[][] | null>(
    null
  );
  const scrollToLineRef = useRef<HTMLDivElement | null>(null);
  const codeRef = useRef<HTMLElement | null>(null);
  const dark = useIsDark();

  useImperativeHandle(
    ref,
    () => {
      return {
        scrollToLine(line: number) {
          if (codeRef.current == null) {
            return;
          }
          const lineElement = codeRef.current.children[line];
          if (lineElement == null) {
            return;
          }
          lineElement.scrollIntoView({ behavior: "smooth", block: "center" });
        },
      };
    },
    [parsedPolicy, codeRef]
  );

  useEffect(() => {
    if (!policy) {
      return;
    }
    polarSyntaxHighlighting(policy, dark).then((parsedPolicy) =>
      setParsedPolicy(parsedPolicy)
    );
  }, [policy, dark]);

  useEffect(() => {
    const lineElement = scrollToLineRef.current;
    if (scrollToLine != null && lineElement != null) {
      lineElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [scrollToLineRef, lines, scrollToLine, parsedPolicy]);

  if (policy == null || policy == "" || parsedPolicy == null) {
    return <div></div>;
  }

  return (
    <pre className="p-[0.5rem] bg-[#f0f5fa] dark:bg-[#1f1f1f] overflow-auto">
      <code className="text-xs whitespace-pre-wrap" ref={codeRef}>
        {parsedPolicy.map((line: ThemedToken[], index: number) => {
          const shouldHighlight = lines && lines.includes(index);
          return (
            <div
              key={index}
              style={
                shouldHighlight
                  ? { backgroundColor: dark ? "#14532d" : "#dbffdb" }
                  : {}
              }
              ref={index === scrollToLine ? scrollToLineRef : null}
            >
              <span className="inline-block text-right select-none text-gray-500 pr-[1em] min-w-[2.25em]">
                {index + 1}
              </span>
              {line.map((token: ThemedToken, index: number) => (
                <span style={{ color: token.color }} key={index}>
                  {token.content}
                </span>
              ))}
            </div>
          );
        })}
      </code>
    </pre>
  );
};

export const Policy = forwardRef(PolicyComponent);

Policy.displayName = "Policy";

type AddedOrRemoved = "added" | "removed";

/**
 * Automatically detects differences as the provided policy changes over time
 * and animates the changes with red and green highlights.
 */
export const DiffPolicy: FunctionComponent<{ policy: string }> = ({
  policy,
}) => {
  const [parsedPolicy, setParsedPolicy] = useState<ThemedToken[][] | null>(
    null
  );
  const [oldPolicy, setOldPolicy] = useState(policy);
  const [lineInfo, setLineInfo] = useState<AddedOrRemoved[]>([]);
  const [renderedPolicy, setRenderedPolicy] = useState<string>(policy);
  const [transition, setTransition] = useState(true);
  const [timeoutID, setTimeoutID] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const dark = useIsDark();

  useEffect(() => {
    if (!renderedPolicy) {
      return;
    }
    polarSyntaxHighlighting(renderedPolicy, dark).then((parsedPolicy) =>
      setParsedPolicy(parsedPolicy)
    );
  }, [renderedPolicy, dark]);

  // in theory this code doesn't need useEffect
  // but a bizarre off-by-2 bug happens without it.
  useEffect(() => {
    const diff = diffLines(oldPolicy, policy);

    // diffLines returns a list of changed and unchanged parts of the policy.
    // if there was no change, the output will just contain one unchanged part.
    // if there was a change, the output will contain more than one element.
    if (diff.length > 1) {
      setOldPolicy(policy);
      const newRenderedPolicy = [];
      let newLineClasses: AddedOrRemoved[] = [];
      let i = 1;
      for (const change of diff) {
        let info: AddedOrRemoved | undefined = change.added
          ? "added"
          : change.removed
          ? "removed"
          : undefined;
        const chunk = change.value.endsWith("\n")
          ? change.value.substring(0, change.value.length - 1)
          : change.value;
        newRenderedPolicy.push(chunk);
        for (
          const prev_i = i;
          i < prev_i + (chunk.split("\n").length || 0);
          i++
        ) {
          info && (newLineClasses[i] = info);
        }
      }

      /*
      We set three state changes in motion:
        1. initial render
        2. animation
        3. cleanup
      In the initial render, we render the HTML as it is before triggering the CSS animations;
      red lines are expanded, green lines are collapsed. 
      In animation, we collapse red lines and expand green lines, and CSS animates it
      In cleanup, we render the final state of the policy and remove green/red parts
      */

      // 1. initial render
      setLineInfo(newLineClasses);
      setRenderedPolicy(newRenderedPolicy.join("\n"));
      setTransition(false);

      // 2. animation
      setTimeout(() => setTransition(true));

      // 3. cleanup
      if (timeoutID) {
        clearTimeout(timeoutID);
      }
      const timeout = setTimeout(() => {
        setRenderedPolicy(policy);
        setLineInfo([]);
      }, 10000);
      setTimeoutID(timeout);
    }
  }, [policy, oldPolicy, timeoutID]);

  if (parsedPolicy == null) {
    return <div></div>;
  }

  return (
    <pre className="m-0 p-2 overflow-auto bg-[rgba(0,83,179,0.05)] dark:bg-[rgba(255,255,255,0.04)]">
      <code className="text-xs whitespace-pre-wrap">
        {parsedPolicy.map((line: ThemedToken[], index: number) => {
          return (
            <div
              key={index}
              style={{
                transitionDuration:
                  lineInfo[index + 1] && transition ? "0.3s" : "0s",
                transitionProperty: "height",
                overflowY: "hidden",
                backgroundColor: lineInfo[index + 1]
                  ? lineInfo[index + 1] === "added"
                    ? dark
                      ? "#14532d"
                      : "#aaffaa"
                    : dark
                    ? "#5e1414"
                    : "#ffaaaa"
                  : dark
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 83, 179, 0.05)",
                height: lineInfo[index + 1]
                  ? transition === (lineInfo[index + 1] === "added")
                    ? "1.25rem" // TODO this doesn't look right for very long lines, but at least it wears off after a few seconds
                    : "0"
                  : undefined,
              }}
            >
              <span className="inline-block text-right select-none text-gray-500 pr-[1em] min-w-[2.25em]">
                {index + 1}
              </span>
              {line.map((token: ThemedToken, index: number) => (
                <span style={{ color: token.color }} key={index}>
                  {token.content}
                </span>
              ))}
            </div>
          );
        })}
      </code>
    </pre>
  );
};

export const StaticDiffPolicy: React.FC<{
  oldPolicy: string;
  newPolicy: string;
  className?: string;
}> = ({ oldPolicy, newPolicy, className }) => {
  const [parsedPolicy, setParsedPolicy] = useState<ThemedToken[][] | null>(
    null
  );
  const dark = useIsDark();

  const diff = diffLines(oldPolicy, newPolicy);

  let lineInfo: AddedOrRemoved[] = [];
  let renderedPolicy = "";

  // diffLines returns a list of changed and unchanged parts of the policy.
  // if there was no change, the output will just contain one unchanged part.
  // if there was a change, the output will contain more than one element.
  if (diff.length > 1) {
    const newRenderedPolicy: string[] = [];
    let newLineClasses: AddedOrRemoved[] = [];
    let lineNumber = 1;
    for (const change of diff) {
      let info: AddedOrRemoved | undefined = change.added
        ? "added"
        : change.removed
        ? "removed"
        : undefined;
      const marker = change.added ? "+" : change.removed ? "-" : " ";

      // Ignore the last newline of the chunk, which jsdiff loves to add
      const chunk = change.value.endsWith("\n")
        ? change.value.substring(0, change.value.length - 1)
        : change.value;
      const lines = chunk.split(/\n/).map((line) => `${marker} ${line}`);
      newRenderedPolicy.push(lines.join("\n"));
      for (
        const prev_i = lineNumber;
        lineNumber < prev_i + (lines.length || 0);
        lineNumber++
      ) {
        info && (newLineClasses[lineNumber] = info);
      }
    }
    lineInfo = newLineClasses;
    renderedPolicy = newRenderedPolicy.join("\n");
  } else {
    renderedPolicy = newPolicy;
  }

  useEffect(() => {
    if (!renderedPolicy) {
      return;
    }
    polarSyntaxHighlighting(renderedPolicy, dark).then((parsedPolicy) =>
      setParsedPolicy(parsedPolicy)
    );
  }, [renderedPolicy, dark]);

  if (parsedPolicy == null) {
    return <div></div>;
  }

  return (
    <pre className={className}>
      <code className="text-xs whitespace-pre-wrap">
        {parsedPolicy.map((line: ThemedToken[], index: number) => {
          return (
            <div
              key={index}
              style={{
                flexWrap: "wrap",
                backgroundColor: lineInfo[index + 1]
                  ? lineInfo[index + 1] === "added"
                    ? dark
                      ? "#14532d"
                      : "#aaffaa"
                    : dark
                    ? "#5e1414"
                    : "#ffaaaa"
                  : dark
                  ? "rgba(255, 255, 255, 0.04)"
                  : "rgba(0, 83, 179, 0.05)",
              }}
            >
              <span className="inline-block text-right select-none text-gray-500 pr-[1em] min-w-[2.25em]">
                {index + 1}
              </span>
              {line.map((token: ThemedToken, index: number) => (
                <span style={{ color: token.color }} key={index}>
                  {token.content}
                </span>
              ))}
            </div>
          );
        })}
      </code>
    </pre>
  );
};
